import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice, showWarning } from 'utils/utils';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { formatOccupancySrt, getFare, getInfantPrice, getSeats } from 'utils/package';
import { PackageDetails } from 'ts/package/interfaces/PackageDetails';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
interface RoomsDetails2Props {
  dealModel: PackageDetails;
  onChangePax: (
    rooms: string[],
    totalPrice: number,
    totalAvgPrice: number,
    infants: string[],
    infantsCount: number
  ) => void;
  rooms: string[];
  infants: string[];
}

interface Room {
  code: string;
  roomType: string;
  price: number;
  currency: string;
  bookCode: number;
  infants: number;
  roomTypes: Select[];
}

const RoomsDetails2: FC<RoomsDetails2Props> = ({
  dealModel: { deal, options, fareBasisBoardDesc, flights },
  onChangePax,
}) => {
  const [t] = useTranslation();

  const seats = getSeats(options, flights);
  const infantPrice = getInfantPrice(options);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalAvgPrice, setTotalAvgPrice] = useState(0);
  const [infantOptions, setInfantOptions] = useState<Select[]>([]);
  const [complects, setComplects] = useState<Select[]>([]);
  const [choosenRooms, setChoosenRooms] = useState<Room[]>([]);

  useEffect(() => {
    if (options.length) {
      const newComplects = options[0].prices
        .map((option) => ({ value: option.complect, label: formatOccupancySrt(option.complect) }))
        .filter((item, index, arr) => arr.findIndex((i) => i.value === item.value) === index);
      const newRoomTypes = options[0].prices
        .filter((item) => item.complect === deal.paxComplect)
        .map((option) => ({
          value: option.roomType + ' ' + getFare(option.fareBasis, fareBasisBoardDesc),
          label: option.roomType + ' ' + getFare(option.fareBasis, fareBasisBoardDesc),
        }))
        .filter((item, index, arr) => arr.findIndex((i) => i.value === item.value) === index);

      const pac = options[0].prices.find(
        (item) => item.complect === deal.paxComplect && item.roomType === deal.roomType
      );
      const newChoosenRooms = [
        {
          code: deal.paxComplect,
          roomType: deal.roomType + ' ' + getFare(deal.fareBasis, fareBasisBoardDesc),
          price: pac?.salePrice || 0,
          choosen: 1,
          currency: pac?.currency || '',
          bookCode: pac?.bookCode || 0,
          infants: 0,
          paxes: deal.paxComplect.length,
          roomTypes: newRoomTypes,
        },
      ];

      if (infantPrice) {
        setInfantOptions([
          { value: 0, label: `0 - ${t('search.infant')}` },
          { value: 1, label: `1 - ${t('search.infant')}` },
          { value: 2, label: `2 - ${t('search.infants')}` },
        ]);
      }
      setComplects(newComplects);
      setChoosenRooms(newChoosenRooms);
      getTotals(newChoosenRooms);
    }
  }, []); // eslint-disable-line

  const onChangeInfants = (value: number, roomIndex: number) => {
    const newRooms = [...choosenRooms];
    newRooms[roomIndex].infants = value;

    const pac = options[0].prices.find(
      (item) =>
        item.complect === newRooms[roomIndex].code &&
        newRooms[roomIndex].roomType.includes(item.roomType)
    );

    newRooms[roomIndex].price = (pac?.salePrice || 0) + value * infantPrice;

    setChoosenRooms(newRooms);
    getTotals(newRooms);
  };

  const getTotals = (newRooms: Room[]) => {
    const newTotalPrice = newRooms.reduce((acc, room) => acc + room.price, 0);
    const newTotalInfants = newRooms.reduce((acc, room) => acc + room.infants, 0);
    const paxCount = newRooms.reduce((acc, room) => acc + room.code.length + room.infants, 0);
    const newTotalAvgPrice = newTotalPrice / paxCount;
    setTotalPrice(newTotalPrice);
    setTotalAvgPrice(newTotalAvgPrice);
    onChangePax(
      newRooms.map((room) => room.bookCode + '-1'),
      newTotalPrice,
      newTotalAvgPrice,
      newRooms.map((room) => room.bookCode + '-' + room.infants),
      newTotalInfants
    );
    return { newTotalPrice, newTotalAvgPrice, newTotalInfants };
  };

  const onClickAdd = () => {
    if (!complects.length) return;
    const defaultComplect = String(complects[0].value);
    const roomTypes = options[0].prices
      .filter((item) => String(item.complect) === defaultComplect)
      .map((option) => ({
        value: option.roomType + ' ' + getFare(option.fareBasis, fareBasisBoardDesc),
        label: option.roomType + ' ' + getFare(option.fareBasis, fareBasisBoardDesc),
      }))
      .filter((item, index, arr) => arr.findIndex((i) => i.value === item.value) === index);
    const defaultRoomType = roomTypes[0]?.value;
    const pac = options[0].prices.find(
      (item) => String(item.complect) === defaultComplect && defaultRoomType.includes(item.roomType)
    );
    const newTotalPaxes =
      choosenRooms.reduce((acc, room) => acc + room.code.length, 0) + defaultComplect.length;
    if (seats && newTotalPaxes > seats) {
      showWarning(t('hotel.max_paxes', { count: seats }));
      return;
    }
    const newRooms = [
      ...choosenRooms,
      {
        code: defaultComplect,
        roomType: defaultRoomType,
        price: pac?.salePrice || 0,
        currency: pac?.currency || '',
        bookCode: pac?.bookCode || 0,
        infants: 0,
        roomTypes,
      },
    ];
    setChoosenRooms(newRooms);
    getTotals(newRooms);
  };

  const onChangeRoom = (value: Room, roomIndex: number) => {
    const roomTypes = options[0].prices
      .filter((item) => item.complect === value.code)
      .map((option) => ({
        value: option.roomType + ' ' + getFare(option.fareBasis, fareBasisBoardDesc),
        label: option.roomType + ' ' + getFare(option.fareBasis, fareBasisBoardDesc),
      }));

    const isRoomType = roomTypes.some((item) => item.value === value.roomType);
    const newRoomType = isRoomType ? value.roomType : roomTypes[0].value;
    const pac = options[0].prices.find(
      (item) => item.complect === value.code && newRoomType.includes(item.roomType)
    );

    const newRoom = {
      ...value,
      price: (pac?.salePrice || 0) + (value.infants || 0) * infantPrice,
      currency: pac?.currency || '',
      bookCode: pac?.bookCode || 0,
      roomTypes,
      roomType: newRoomType,
    };

    const newRooms = [
      ...choosenRooms.slice(0, roomIndex),
      { ...newRoom },
      ...choosenRooms.slice(roomIndex + 1),
    ];

    if (seats && newRooms.reduce((acc, room) => acc + room.code.length, 0) > seats) {
      showWarning(t('hotel.max_paxes', { count: seats }));
      return;
    }

    setChoosenRooms(newRooms);
    getTotals(newRooms);
  };

  const onClickRemove = (roomIndex: number) => {
    const newRooms = [...choosenRooms];
    newRooms.splice(roomIndex, 1);
    setChoosenRooms(newRooms);
  };

  return (
    <div className="package-rooms">
      <div className="warning color-warning">
        <div>{t('hotel.please_note')}</div>
        <div>{t('hotel.you_can_choose', { count: seats })}</div>
      </div>

      <div className="table-default mb-20 mt-20">
        <table className="table-bordered-lines table-mobile">
          <thead>
            <tr>
              <th>{t('package.room_no')}</th>

              <th>{t('package.complect')}</th>
              <th>{t('package.room_type_accommodation_base')}</th>
              {!!infantPrice && (
                <th>
                  {t('search.infants')} ({t('search.under')} 2){' '}
                  <span className="text-light text-xsm">
                    ({formatPrice(infantPrice, deal.currency)})
                  </span>
                </th>
              )}
              <th>{t('price')}</th>
            </tr>
          </thead>
          <tbody>
            {choosenRooms.map((item, roomIndex) => {
              return (
                <tr key={roomIndex}>
                  <td>
                    <span className="text-bold flex-row">
                      <span>{roomIndex + 1}</span>{' '}
                      <span className="visible-tablet ml-5">{t('package.room_selection')}</span>
                    </span>
                  </td>
                  <td width={200}>
                    <SelectCustom
                      options={complects}
                      value={item.code}
                      onChange={(val) => onChangeRoom({ ...item, code: val }, roomIndex)}
                      appendToBody
                    />
                  </td>
                  <td width={400}>
                    <SelectCustom
                      options={item.roomTypes}
                      value={item.roomType}
                      onChange={(val) => onChangeRoom({ ...item, roomType: val }, roomIndex)}
                      appendToBody
                    />
                  </td>
                  {!!infantPrice && (
                    <td width={190}>
                      <div className="flex-row">
                        <span className="mr-5">
                          <SelectCustom
                            appendToBody
                            options={infantOptions}
                            value={item.infants}
                            onChange={(val) => onChangeInfants(val, roomIndex)}
                          />
                        </span>
                      </div>
                    </td>
                  )}
                  <td className="pl-10 text-bold text-contrast">
                    <span className="text-nowrap">
                      {formatPriceCurrency(item.price, getCurrency(item.currency))}
                      {roomIndex > 0 && (
                        <FontAwesomeIcon
                          icon={faTimes}
                          className="ml-5 text-black cur-poi"
                          onClick={() => onClickRemove(roomIndex)}
                        />
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex-between ph-10">
        <div className="flex-row-end mb-20 text-sm cur-poi" onClick={onClickAdd}>
          <FontAwesomeIcon icon={faPlusCircle} className="mr-5 text-contrast" />
          {t('package.add_room_to_reservation')}
        </div>
      </div>

      <div className="flex-between">
        <div>
          {!!totalAvgPrice && !!totalPrice && (
            <Fragment>
              <div className="text-st text-contrast text-bold mb-5">
                {t('flight.total_average_passenger')}:{' '}
                {formatPriceCurrency(totalAvgPrice, getCurrency(deal.currency))}
              </div>
              <div className="text-xsm text-light">
                {t('total')}: {formatPriceCurrency(totalPrice, getCurrency(deal.currency))}
              </div>
            </Fragment>
          )}
        </div>
        <div className="text-md text-bold">
          {t('hotel.rooms')}: {choosenRooms.length}
        </div>
      </div>
    </div>
  );
};

export default RoomsDetails2;
